import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import DeleteModal from "../../components/common/deleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import BreadCrumb from "../../components/common/breadCrumb";
import {
  addHrRaiseQuery,
  viewSupport,
} from "../../api/supportApi";
import TableContainer from "../../components/common/tableContainer";
import { TypeOfIssue, ResourceType } from "../../utils/helpers/enums";
import Loader from "../../components/common/loader";
import { getId, getRole } from "../../utils/helpers/custom";
import { checkStatusCodeSuccess, CommonLabel, dateFormat, errorHandler, HandleResponse, Roles, TableSortFunction } from "../../utils/helpers/common";
import { findDevicesNumber, listOfData } from "../../api/commonApi"
import NoResultsMessage from "../../components/noResultMessage";
import { assignDeviceOfNumber } from "../../api/resourcesApi";
import { SupportLabel } from "../../common/constant/Support";
import { InputPlaceHolder, SelectPlaceHolder, validationMessages } from "../../utils/helpers/validationMasseges";
import { Resources, ResourceStatus } from "../../common/constant/Resource";
import { BaseSelect } from "../../components/BaseComponents/BaseSelect";
import BaseInput from "../../components/BaseComponents/BaseInput";
import BaseModal from "../../components/BaseComponents/BaseModal";
import BaseButton from "../../components/BaseComponents/BaseButton";

const Status = ({ status }) => {
  switch (status) {
    case SupportLabel.InProgress:
      return (
        <span className="badge badge-soft-warning text-uppercase">
          {status}
        </span>
      );
    case CommonLabel.Pending:
      return (
        <span className="badge badge-soft-danger text-uppercase">{status}</span>
      );
    case SupportLabel.Completed:
      return (
        <span className="badge badge-soft-success text-uppercase">
          {status}
        </span>
      );
    default:
      return (
        <span className="badge badge-soft-success text-uppercase">
          {status}
        </span>
      );
  }
};

const Support = () => {
  document.title = SupportLabel.HrSupport;
  const id = getId("id");
  const role = getRole();
  const [viewSupportList, setViewSupportList] = useState([]);
  const [designation, setDesignation] = useState([]);
  const [listOfDataHR, setListOfDataHR] = useState();
  const [modalLoader, setModalLoader] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);

  const fetchData = () => {
    setTableLoader(true);
    viewSupport(id)
      .then((response) => {
        setViewSupportList(response?.data?.data);
      })
      .catch((err) => err)
      .finally(() => {
        setTableLoader(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    setLoading(true);
    let condition = role === Roles.HR ? {role: Roles.Admin}:{
      role: Roles.HR
    }
    listOfData({
      modelName: "authUserModel",
      condition,
      selectionCriteria: ["id","firstName","lastName"]
    }).then((response) => {
      setListOfDataHR(response?.data?.map((res) => ({
          value: `${res?.id}`,
          label: `${res?.firstName} ${res?.lastName}`,
      })))
    }).catch((err) => { return (err); }).finally(() => { setLoading(false) }).finally(() => { setLoading(false) });
  }, [])

  const [deleteModal, setDeleteModal] = useState(false);
  
  const columns = useMemo(() => [
    {
      Header: SupportLabel.TypeOfIssue,
      accessor: "supportIssue",
      filterable: false,
    },
    {
      Header: SupportLabel.DeviceName,
      accessor: "resourcesDetails.resourceName",
      filterable: false,
      Cell: (items) => {
        return (
          <div>
            {items?.row?.original?.resourcesDetails?.resourceName || HandleResponse.NoData}
          </div>
        );
      },
    },
    {
      Header: SupportLabel.ResourceNo,
      accessor: "resourcesDetails.resourceNo",
      filterable: false,
      Cell: (items) => {
        return (
          <div>
            {items?.row?.original?.resourcesDetails?.resourceNo || HandleResponse.NoData}
          </div>
        );
      },
    },
    {
      Header: SupportLabel.HrName,
      accessor: "assignToUser",
      filterable: false,
      Cell: (cell) => {
        return `${cell?.row?.original?.assignToUser?.firstName} ${cell?.row?.original?.assignToUser?.lastName}`;
      },
      sortType: (rowA, rowB) =>
        TableSortFunction(rowA, rowB, (row) =>
        `${row?.original?.assignToUser?.firstName} ${row?.original?.assignToUser?.lastName}`.toLowerCase()
      ),
    },
    {
      Header: SupportLabel.Issue,
      accessor: "reason",
      filterable: false,
    },
    {
      Header: SupportLabel.DateOfRequest,
      accessor: "dateOfRequest",
      filterable: false,
      Cell: (items) => {
        return <div>{dateFormat(items?.row?.original?.dateOfRequest)}</div>;
      },
    },
    {
      Header: CommonLabel.Status,
      accessor: "status",
      filterable: false,
      Cell: (cell) => {
        return <Status status={cell?.value} />;
      },
    },
  ]);

  const [todo, setTodo] = useState(null);
  const [modalTodo, setModalTodo] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = useCallback(() => {
    if (modalTodo) {
      viewSupport(id).then((response) => { setViewSupportList(response?.data?.data) })
      setModalTodo(false);
      validation.resetForm({ values: validation.initialValues });
      setTodo("");
      setTodo(null);
    } else {
      setModalTodo(true);
      validation.resetForm({ values: validation.initialValues });
    }
  }, [modalTodo]);

  const handleTodoClicks = () => {
    setTodo("");
    setModalTodo(!modalTodo);
    toggle();
  };
  const handleDeleteTodo = () => {
    if (todo) {
      setDeleteModal(false);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      resourceName: (todo && todo.device) || "",
      resourceId: (todo && todo.id) || "",
      reason: (todo && todo.reason) || "",
      issueId: (todo && todo.id) || "",
      assignToHR: (todo && todo.assignToHR) || ""
    },
    validationSchema: Yup.object({
      resourceName: Yup.string().when("issueId", {
        is: (resourceName) => [SupportLabel.DeviceIssue].includes(resourceName), 
        then: Yup.string().required(validationMessages.required(SupportLabel.Device)),
        otherwise: Yup.string().notRequired(),
      }),
      reason: Yup.string().required(validationMessages.required(SupportLabel.Issue)),
      issueId: Yup.string().required(validationMessages.required(SupportLabel.TypeOfIssue)),
      assignToHR: Yup.string().required(validationMessages.required(Roles.HR)),
      resourceId: Yup.string().when(['issueId', 'resourceName'], (issueId, resourceName, schema) => {
        if ((issueId === SupportLabel.DeviceIssue)) {
          return schema.required(validationMessages.required(SupportLabel.ResourceNo));
        } else {
          return schema.notRequired();
        }
      }),
      
    }),
    onSubmit: (values) => {
      setModalLoader(true);
      addHrRaiseQuery({
        resourceNo: values.resourceId,
        resourceName: values.resourceName,
        supportIssue: values.issueId,
        reason: values.reason,
        userId: parseInt(id),
        assignTo: parseInt(values.assignToHR)
      })
        .then((response) => {
          if (checkStatusCodeSuccess(response?.data?.statusCode)) {
            toast.success(response?.data?.message);
            setModalTodo(false);
            setTodo("");
            validation.resetForm();
            fetchData();
          } else {
            toast.error(response?.data?.message);
          }
        })
        .catch((err) => {
          errorHandler(err);
          return err;
        })
        .finally(() => {
          setModalLoader(false);
        });
      },
  });

  function handleMultipleDevice(selectedDevice) {
    assignDeviceOfNumber({
      resourceName: selectedDevice?.value,
      status: [ResourceStatus.Allocation],
      assignTo: +id
    })
      .then((resp) => {
        if (selectedDevice) {
          setDesignation(resp?.data?.data?.map((res) => ({
            value: res?.resourcesDetails?.resourceNo,
            label: res?.resourcesDetails?.resourceNo,
          })
        ));
        } else {
          setDesignation([]);
        }
      })
      .catch((err) => err)
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => handleDeleteTodo()}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
          <Container fluid>
            <BreadCrumb title={SupportLabel.Support} pageTitle="HRMS" />
            <Row className="mb-2">
              <div className="col-sm order-3 order-sm-2 mt-3 mt-sm-0">
                <h5 className="fw-semibold mt-2">{SupportLabel.SupportRequest}</h5>
              </div>
              <div className="col-auto order-2 order-sm-3 ms-auto">
                <div className="hstack gap-2">
                  <BaseButton
                    color="primary"
                    type="button"
                    onClick={() => handleTodoClicks()}
                  >
                    {SupportLabel.SupportRequest}
                  </BaseButton>
                </div>
              </div>
            </Row>
            <Card>
              <CardBody>
                {(viewSupportList?.length > 0 || tableLoader) ? (
                  <TableContainer
                    columns={columns}
                    data={(viewSupportList || [])}
                    isAddUserList={false}
                    isGlobalFilter={true}
                    customPageSize={5}
                    tableLoader={tableLoader}
                    divClass="table-responsive table-card mb-1"
                    tableClass="text-center table-wrap"
                    theadClass="table-light text-muted"
                  />
                ) : (
                  <NoResultsMessage/>
                )}
              </CardBody>
            </Card>
          </Container>
      </div>
      <BaseModal
        open={modalTodo}
        onClose={() => setModalTodo(false)}
        toggle={toggle}
        className="zoomIn"
        title={SupportLabel.SupportRequest}
        bodyClassName="p-4"
        submit={validation.handleSubmit}
        submitLabel={CommonLabel.Submit}
        submitLabelColor="primary"
        closeLabel={CommonLabel.Close}
        closeLabelColor="ghost-success"
        loader={modalLoader}
      >
        <div id="task-error-msg" className="alert alert-danger py-2"></div>
        <Form
          id="creattask-form"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <div className="mb-3">
            <BaseSelect
              placeholder={SelectPlaceHolder(SupportLabel.TypeOfIssue)}
              label={SupportLabel.TypeOfIssue}
              name="issueId"
              options={TypeOfIssue}
              value={validation.values.issueId}
              handleChange={(field, value) => {
                validation.setFieldValue(field, value);
              }}
              handleBlur={validation.handleBlur}
              error={validation.errors.issueId}
              touched={validation.touched.issueId}
              required
            />
          </div>

          {validation.values.issueId === SupportLabel.DeviceIssue && (
            <>
              <div className="mb-3">
                <BaseSelect
                  placeholder={SelectPlaceHolder(SupportLabel.Device)}
                  label={SupportLabel.Device}
                  name="resourceName"
                  options={ResourceType}
                  value={validation.values.resourceName}
                  handleChange={(field, value) => {
                    validation.setFieldValue(field, value);
                    handleMultipleDevice(value);
                  }}
                  handleBlur={validation.handleBlur}
                  error={validation.errors.resourceName}
                  touched={validation.touched.resourceName}
                  required
                />
              </div>

                <div className="mb-3">
                  <BaseSelect
                    placeholder={SelectPlaceHolder(SupportLabel.ResourceNo)}
                    label={SupportLabel.ResourceNo}
                    name="resourceId"
                    options={designation}
                    value={validation.values.resourceId}
                    handleChange={(field, value) => {
                      validation.setFieldValue(field, value);
                    }}
                    handleBlur={validation.handleBlur}
                    error={validation.errors.resourceId}
                    touched={validation.touched.resourceId}
                    required
                  />
                </div>
            </>
          )}

          <div className="mb-3">
            <BaseSelect
              placeholder={SelectPlaceHolder(Roles.HR)}
              label={Roles.HR}
              name="assignToHR"
              options={listOfDataHR}
              value={validation.values.assignToHR}
              handleChange={(field, value) => {
                validation.setFieldValue(field, value);
              }}
              handleBlur={validation.handleBlur}
              error={validation.errors.assignToHR}
              touched={validation.touched.assignToHR}
              required
            />
          </div>

          <div className="mb-3">
            <BaseInput
              placeholder={InputPlaceHolder(SupportLabel.Issue)}
              label={SupportLabel.Issue}
              type="text"
              name="reason"
              onChange={validation.handleChange}
              handleBlur={validation.handleBlur}
              value={validation.values.reason}
              error={validation.errors.reason}
              touched={validation.touched.reason}
              required
            />
          </div>
        </Form>
      </BaseModal>
    </React.Fragment>
  );
};

export default Support;
